<template>
    <div v-if="normalizedContent">
        <!-- Legal agreement HTML sanitized with DOMPurify. -->
        <!-- eslint-disable vue/no-v-html -->
        <div class="legal-agreement-pdf-content" v-html="normalizedContent" />
        <!-- eslint-enable vue/no-v-html -->
        <!-- Chromium never fetches an image only used in page margins: without this, the footer loses its logo. -->
        <img :src="MALT_LOGO_URL" alt="" hidden />
    </div>
    <div v-else>An error occurred while generating document. Please contact support.</div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useHead, useRequestEvent} from '#imports';
    import {useSanitizeHtml} from '@malt/utils';
    import {LEGAL_AGREEMENT_PDF_STYLESHEET, MALT_LOGO_URL} from '~/utils/legalAgreementPdfStylesheet';
    import {resolveLegalAgreementVariables} from '~/utils/legalAgreementVariables';

    interface Props {
        content: string;
        fileName: string;
        variableValues?: Record<string, string>;
    }

    const {content, fileName, variableValues} = defineProps<Props>();
    const {sanitizeHtml} = useSanitizeHtml();
    const requestEvent = useRequestEvent();

    // The PDF renderer runs with JavaScript disabled, so styles only apply when they ship inside the markup.
    useHead({style: [{textContent: LEGAL_AGREEMENT_PDF_STYLESHEET}]});

    if (requestEvent) {
        requestEvent.context.pdfFileName = fileName;
    }

    const normalizedContent = computed<string>(() => {
        const spacedContent = content
            .replace(/&nbsp;/gi, ' ')
            .replace(/&#160;/g, ' ')
            .replace(/\u00A0/g, ' ');
        return sanitizeHtml(variableValues ? resolveLegalAgreementVariables(spacedContent, variableValues) : spacedContent);
    });
</script>
